import React, { useEffect } from 'react';
import moment from 'moment';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  View,
  Layout,
  Card,
  Stack,
  Spinner,
  HelpLink,
  withTranslation,
  Text,
  Icon,
} from '@piwikpro/ui-components';
import {
  view,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { TrafficMonitor } from '@piwikpro/traffic-monitor-crate';
import { BillingService } from '@piwikpro/marketing-crate';
import { AuditLogService } from '@piwikpro/audit-log-crate';
import { Apps } from '@piwikpro/apps-crate';
import { OrganizationState } from '@piwikpro/organization-crate';
import { TrafficMonitorState } from '@piwikpro/traffic-monitor-crate/types';
import { AuditLogState } from '@piwikpro/audit-log-crate/reducer';
import LicenseDetailsPlan from './LicenseDetailsPlan';
import LicenseDetailsAccount from './LicenseDetailsAccount';
import LicenseDetailsTrafficUsage from './LicenseDetailsTrafficUsage';
import LicenseDetailsDataRetention from './LicenseDetailsDataRetention';
import { AdministrationState } from '../../../../redux';

const mapStateToProps = (
  state: {
    organization: OrganizationState
    trafficMonitor: TrafficMonitorState
    administration: AdministrationState['administration']
    auditLog: AuditLogState
  },
) => ({
  isLoading: state.organization.details.isLoading
    || state.trafficMonitor.usage?.isLoading
    || state.administration.apps.list.isLoading
    || state.auditLog.retention.isLoading,
  failed: state.organization.details.failed
    || state.trafficMonitor.usage?.failed
    || state.administration.apps.list.failed
    || state.auditLog.retention.failed,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  trafficMonitor: TrafficMonitor
  apps: Apps
  auditLog: AuditLogService
  Title: typeof React.Component
  ScheduledActionInformation: React.ComponentType<Record<string, never>>
  t: TFunction
  billingService: BillingService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
  tReady: boolean
}

export const LicenseDetails = ({
  trafficMonitor,
  auditLog,
  apps,
  failed,
  isLoading,
  Title,
  t,
  tReady,
  billingService,
  ScheduledActionInformation,
}: Props) => {
  const fetchAllData = async () => {
    try {
      await Promise.all([
        billingService.getCurrentSubscriptionTier(),
        billingService.getTiers(),
        trafficMonitor.getTrafficUsage({
          from: moment.utc().subtract(1, 'months').format(
            'YYYY-MM',
          ),
          to: moment.utc().format(
            'YYYY-MM',
          ),
        }),
        apps.find(),
        auditLog.getRetention(),
      ]);
      // eslint-disable-next-line no-empty
    } catch { }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  if (isLoading || !tReady) {
    return <Spinner type="centered" className="x-large" />;
  }

  return (
    <Title part={t('platform.license.title')}>
      <Layout maxWidth="wide">
        <View>
          <View.Header title={t('platform.license.title')} />
          <View.Body>
            <Stack horizontalAlignment="middle" vertical>
              <Stack.Item fill>
                <ScheduledActionInformation />
              </Stack.Item>
              <Stack.Item fill>
                <Card>
                  {failed ? (
                    <Card.Section>
                      <Stack
                        vertical
                        horizontalAlignment="middle"
                        verticalAlignment="center"
                      >
                        <Stack.Item>
                          <Stack
                            vertical
                            horizontalAlignment="middle"
                          >
                            <Stack.Item>
                              <Icon
                                color="red"
                                name="exclamation-mark-circle"
                                size="medium"
                              />
                            </Stack.Item>
                            <Stack.Item previousSpacing="narrow">
                              <Text color="Red" dataId="TextStyle">
                                {t('accountSecurity.twoFactorAuth.load.failed.description')}
                              </Text>
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                  ) : (
                    <>
                      <Card.Section padding="none">
                        <LicenseDetailsPlan />
                      </Card.Section>
                      <Card.Section>
                        <LicenseDetailsAccount />
                        <LicenseDetailsDataRetention />
                        <LicenseDetailsTrafficUsage />
                      </Card.Section>
                    </>
                  )}
                </Card>
              </Stack.Item>
              <Stack.Item>
                <HelpLink href="https://piwik.pro/changelog/">
                  Changelog
                </HelpLink>
              </Stack.Item>
            </Stack>
          </View.Body>
        </View>
      </Layout>
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  trafficMonitor: 'TrafficMonitorCrate.trafficMonitor',
  auditLog: 'AuditLogCrate.auditLog',
  apps: 'AppsCrate.apps',
  Title: 'TitleCrate.components.Title',
  billingService: 'MarketingCrate.billingService',
  ScheduledActionInformation: 'MarketingCrate.components.ScheduledActionInformation',
})(view({ role: 'OWNER' })(withTranslation(['administration'])(
  errorBoundary(LicenseDetails as any),
))));
